import { Button, CloseButton } from 'components';
import { noop } from 'lodash/fp';
import React, {
  CSSProperties,
  FC,
  KeyboardEvent,
  MutableRefObject,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import {
  ButtonContainer,
  FooterActions,
  Message,
  RadioGroup,
  RadioInput,
  RadioLabel,
  RadioMark,
  RadioOption,
  selectionActionButtonStyles,
  StyledPopup,
  Wrapper,
} from './styled';

export interface PopupSelectionOption {
  value: string;
  label: string;
  handleConfirm: () => void;
}

interface PopupEventProps {
  wrapperRef?: MutableRefObject<any>;
  popupMessageText?: string;
  popupButtons?: any[];
  buttonContainerStyles?: any;
  popupStyles: CSSProperties;
  innerComponent: any;
  closePopup?: () => void;
  darkBackground?: boolean;
  extraMessageStyle?: CSSProperties;
  selectionOptions?: PopupSelectionOption[];
  defaultSelectionValue?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  hideCloseButton?: boolean;
}

const PopupEvent: FC<PopupEventProps> = ({
  popupMessageText,
  popupButtons = [],
  buttonContainerStyles,
  popupStyles,
  innerComponent,
  closePopup = noop,
  wrapperRef,
  darkBackground = true,
  extraMessageStyle,
  selectionOptions,
  defaultSelectionValue,
  cancelButtonText,
  confirmButtonText,
  hideCloseButton,
}) => {
  const { t } = useTranslation();
  const isSelectionMode = Boolean(selectionOptions?.length);
  const shouldHideCloseButton = hideCloseButton ?? isSelectionMode;

  const [selectedValue, setSelectedValue] = useState(
    () =>
      defaultSelectionValue ||
      (selectionOptions && selectionOptions[0]?.value) ||
      '',
  );

  useEffect(() => {
    if (isSelectionMode && wrapperRef?.current?.focus) {
      wrapperRef.current.focus();
    }
  }, [isSelectionMode, wrapperRef]);

  const handleConfirmAndClose = (confirmCallback) => {
    confirmCallback();
    closePopup();
  };

  const handleSelectionConfirm = () => {
    const selectedOption = selectionOptions.find(
      (option) => option.value === selectedValue,
    );

    if (selectedOption) {
      handleConfirmAndClose(selectedOption.handleConfirm);
    }
  };

  const moveSelection = (direction: 1 | -1) => {
    if (!selectionOptions?.length) {
      return;
    }

    const currentIndex = selectionOptions.findIndex(
      (option) => option.value === selectedValue,
    );
    const nextIndex =
      (currentIndex + direction + selectionOptions.length) %
      selectionOptions.length;

    setSelectedValue(selectionOptions[nextIndex].value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isSelectionMode) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      moveSelection(1);
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      moveSelection(-1);
      return;
    }

    if (event.key === 'Enter') {
      const target = event.target as HTMLElement;

      if (target.tagName === 'BUTTON') {
        return;
      }

      event.preventDefault();
      handleSelectionConfirm();
    }
  };

  return (
    <Wrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      darkBackground={darkBackground}
    >
      <StyledPopup
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        ref={wrapperRef}
        id="popUpWrapper"
        popupStyles={popupStyles}
        $isSelectionMode={isSelectionMode}
        tabIndex={isSelectionMode ? 0 : undefined}
        onKeyDown={handleKeyDown}
      >
        {!shouldHideCloseButton && <CloseButton onClick={closePopup} />}
        {popupMessageText && (
          <Message style={extraMessageStyle} $isSelectionMode={isSelectionMode}>
            {popupMessageText}
          </Message>
        )}
        {innerComponent}
        {isSelectionMode ? (
          <>
            <RadioGroup role="radiogroup">
              {selectionOptions.map((option) => {
                const isChecked = selectedValue === option.value;

                return (
                  <RadioOption key={option.value} $checked={isChecked}>
                    <RadioInput
                      type="radio"
                      name="popup-event-selection"
                      value={option.value}
                      checked={isChecked}
                      onChange={() => setSelectedValue(option.value)}
                      tabIndex={-1}
                    />
                    <RadioMark />
                    <RadioLabel>{option.label}</RadioLabel>
                  </RadioOption>
                );
              })}
            </RadioGroup>
            <FooterActions>
              <Button
                type="button"
                variant="secondary"
                buttonStyles={selectionActionButtonStyles}
                onClick={closePopup}
              >
                {cancelButtonText || t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="default"
                buttonStyles={selectionActionButtonStyles}
                onClick={handleSelectionConfirm}
              >
                {confirmButtonText || t('event.eventOperations.confirm')}
              </Button>
            </FooterActions>
          </>
        ) : (
          popupButtons && (
            <ButtonContainer buttonContainerStyles={buttonContainerStyles}>
              {popupButtons.map((button) => (
                <Button
                  key={uuid()}
                  width="80%"
                  type="button"
                  variant={button.variant || 'default'}
                  buttonStyles={button.buttonStyles}
                  IconComponent={button.IconComponent || null}
                  onClick={
                    button.handleConfirm === noop
                      ? closePopup
                      : () => handleConfirmAndClose(button.handleConfirm)
                  }
                >
                  {button.popupButtonText}
                </Button>
              ))}
            </ButtonContainer>
          )
        )}
      </StyledPopup>
    </Wrapper>
  );
};

export default PopupEvent;
